import React, { useEffect, useState, useCallback } from "react"
import { TextField, Button, Typography, Snackbar, Alert } from "@mui/material"
import Box from "@mui/material/Box"
import { styled } from "@mui/system"
import { useNavigate } from "react-router-dom"
import { useSignIn } from "react-auth-kit"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import Visibility from "@mui/icons-material/Visibility"
import VisibilityOff from "@mui/icons-material/VisibilityOff"
import api from "../utils/api"

const CenteredBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "16px",
  marginTop: "32px",
})

export const AdminLoginForm: React.FC<{ notification?: string }> = ({
  notification = "",
}) => {
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const navigate = useNavigate()
  const signIn = useSignIn()
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "info" | "warning"
  >("success")
  const [showPassword, setShowPassword] = useState(false)

  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false)
    setSnackbarMessage(null)
  }, [])

  useEffect(() => {
    if (notification) {
      setSnackbarMessage(notification)
      setSnackbarSeverity("success")
      setSnackbarOpen(true)
    }
  }, [notification])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    try {
      const response = await api.post("/admin/login", { username, password })
      const data = response.data

      signIn({
        token: data.token,
        expiresIn: 3600,
        tokenType: "Bearer",
        authState: {
          username: data.username,
          type: data.type,
          name: data.name,
          eventId: data.eventId,
          id: data.id,
        },
      })
      navigate("/admin/dashboard", {
        state: { notification: "Login successful!" },
      })
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Invalid Credentials 🙁. Please try again."
      setSnackbarMessage(errorMessage)
      setSnackbarSeverity("error")
      setSnackbarOpen(true)
    }
  }

  return (
    <CenteredBox>
      <Typography variant="h5">Admin Login</Typography>
      <form onSubmit={handleSubmit}>
        <TextField
          label="Username"
          variant="outlined"
          value={username}
          onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Password"
          type={showPassword ? "text" : "password"}
          variant="outlined"
          value={password}
          onChange={(e) => setPassword(e.target.value.replace(/\s/g, ""))}
          fullWidth
          margin="normal"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button type="submit" variant="contained" color="primary">
          Login
        </Button>
      </form>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={snackbarSeverity === "error" ? 6000 : 3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </CenteredBox>
  )
}
